import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, switchMap, tap, catchError, of, withLatestFrom, map, retryWhen, concatMap, throwError, timer } from 'rxjs';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class PatientAdmissionHistoryService {
  private apiUrl = `api/admission-record`;
  private patientAHSubject = new BehaviorSubject<any[] | null>(null);
  private patientWardChangeSubject = new BehaviorSubject<any[] | null>(null);
  private editingAHSubject = new BehaviorSubject<any>(null);

  patientAdmissionRecords$ = this.patientAHSubject.asObservable();
  patientWardChanges$ = this.patientWardChangeSubject.asObservable();
  editingAH$ = this.editingAHSubject.asObservable();

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          // Clear data synchronously before async load starts
          this.patientAHSubject.next([]);
          this.patientWardChangeSubject.next([]);
          return this.loadAdmissionHistoryByPatient(patient.id).pipe(
            retryWhen(errors =>
              errors.pipe(
                concatMap((error, index) => {
                  // Don't retry for client errors (4xx) except 408 (timeout) and 429 (rate limit)
                  const status = error?.status;
                  const shouldRetry = !status || (status >= 500) || status === 408 || status === 429;
                  
                  if (!shouldRetry || index >= 2) {
                    // Max retries exceeded or non-retryable error
                    return throwError(() => error);
                  }
                  
                  const delayTime = 1000 * Math.pow(2, index); // Exponential backoff: 1s, 2s
                  console.warn(`Retrying admission history load (attempt ${index + 1}/3) after ${delayTime}ms`, error);
                  return timer(delayTime);
                })
              )
            ),
            catchError((error) => {
              console.error('Error loading patient admission history after retries:', error);
              // Return empty data on error to clear stale data
              return of({ data: [], wardChanges: [] });
            })
          );
        } else {
          this.patientAHSubject.next([]);
          this.patientWardChangeSubject.next([]);
          return of({ data: [], wardChanges: [] }); // Return an observable with empty arrays
        }
      }),
      tap((results: any) => {
        const history = results?.data ?? [];
        this.patientAHSubject.next(history);
        const wardChanges = results?.wardChanges ?? [];
        this.patientWardChangeSubject.next(wardChanges);
      }),
      catchError((error) => {
        console.error('Error in patient admission history subscription:', error);
        this.patientAHSubject.next([]);
        this.patientWardChangeSubject.next([]);
        return of({ data: [], wardChanges: [] });
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {
    // Listener for when a new admission record is added - use reactive patient ID tracking
    this.socketService.listen('admissionRecordAdded', 'ah-service').pipe(
      withLatestFrom(this.patientService.currentPatient$),
      map(([item, currentPatient]) => {
        // Only process if record belongs to current patient
        const currentPatientId = currentPatient?.id;
        if (item.patientId === currentPatientId && currentPatientId) {
          return { item, currentPatientId };
        }
        return null;
      })
    ).subscribe(result => {
      if (result) {
        // Double-check patient ID matches before adding (defense against race conditions)
        if (result.item.patientId !== result.currentPatientId) {
          return;
        }
        const current = this.patientAHSubject.getValue() || [];
        this.patientAHSubject.next([...current, result.item]);
      }
    });

    // Listener for when an admission record is updated - use reactive patient ID tracking
    this.socketService.listen('admissionRecordUpdated', 'ah-service').pipe(
      withLatestFrom(this.patientService.currentPatient$),
      map(([item, currentPatient]) => {
        // Only process if record belongs to current patient
        const currentPatientId = currentPatient?.id;
        if (item.patientId === currentPatientId && currentPatientId) {
          return { item, currentPatientId };
        }
        return null;
      })
    ).subscribe(result => {
      if (result) {
        // Double-check patient ID matches before processing (defense against race conditions)
        if (result.item.patientId !== result.currentPatientId) {
          return;
        }
        
        const current = this.patientAHSubject.getValue() || [];
        const recordIndex = current.findIndex(ah => ah.id === result.item.id);
        
        if (recordIndex >= 0) {
          // Record exists, update it
          const updated = current.map(ah => ah.id === result.item.id ? result.item : ah);
          this.patientAHSubject.next(updated);
        } else if (result.item.active) {
          // Record doesn't exist but is now active (restored), add it to the list
          const updated = [...current, result.item];
          this.patientAHSubject.next(updated);
        }
      }
    });
  }

  loadAdmissionHistoryByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getAdmissionRecordById(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateAdmissionHistory(id: string, formData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any[]>(`${this.apiUrl}/update/${id}`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  setEditingAH(item: any | null) {
    this.editingAHSubject.next(item);
  }
}
